import React, { useEffect, useMemo, useState } from "react";
import { Loader2, AtSign, Send, Trash2 } from "lucide-react";
import { authedApi } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useToast } from "../../hooks/use-toast";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const TeamChatPanel = ({ token }) => {
  const { user, adminName, adminToken } = useAuth();
  const myName = adminName || user?.habbo_name;
  const isAdmin = !!adminToken;
  const a = useMemo(() => authedApi(token), [token]);
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setMessages(await a.listTeamChat(200)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const i = setInterval(load, 15_000);
    return () => clearInterval(i);
  }, []);

  const onSend = async () => {
    if (!text.trim()) return;
    try { await a.postTeamChat(text.trim()); setText(""); await load(); }
    catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" }); }
  };

  const onDelete = async (m) => {
    if (!window.confirm(`Nachricht von ${m.author} loeschen?`)) return;
    try {
      await a.deleteTeamChat(m.id);
      toast({ title: "Geloescht" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    }
  };

  const renderText = (t) =>
    t.split(/(@[\w\-\.:\-]+)/g).map((part, i) =>
      part.startsWith("@")
        ? <span key={i} className="rounded bg-[#7c4dff]/20 px-1 font-semibold text-[#b39bff]">{part}</span>
        : <React.Fragment key={i}>{part}</React.Fragment>
    );

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0f141b]" data-testid="team-chat">
      <div className="border-b border-white/10 px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-300">
          <AtSign className="h-4 w-4" /> Team-Chat
        </h3>
        <div className="text-[11px] text-zinc-500">
          Mit <span className="text-[#b39bff]">@name</span> Team-Mitglieder erwähnen – sie bekommen eine Benachrichtigung.
        </div>
      </div>
      <div className="max-h-[60vh] space-y-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>
        ) : messages.length === 0 ? (
          <div className="py-8 text-center text-xs text-zinc-500">Noch keine Nachrichten.</div>
        ) : messages.map((m, idx) => {
          const prev = messages[idx - 1];
          const grouped = prev && prev.author === m.author && (new Date(m.created_at) - new Date(prev.created_at) < 2 * 60 * 1000);
          const isMe = m.author === myName;
          const canDelete = isMe || isAdmin;
          const glow = m.user_meta?.custom_glow_color;
          const badge = m.project_badge;
          return (
            <div key={m.id} data-testid={`team-chat-msg-${m.id}`} className={`group/msg flex gap-2 ${grouped ? "mt-0.5" : "mt-2"}`}>
              <div className="w-8 flex-shrink-0">
                {!grouped && (
                  <div className="group relative h-8 w-8">
                    {glow && (
                      <div
                        aria-hidden
                        className="comment-glow always-pulse absolute left-1/2 top-1/2 h-12 w-12 rounded-full pointer-events-none"
                        style={{ background: `radial-gradient(circle, ${glow}ff 0%, ${glow}66 40%, ${glow}00 75%)`, transform: "translate(-50%, -50%)" }}
                      />
                    )}
                    <img
                      src={`https://www.habbo.de/habbo-imaging/avatarimage?user=${encodeURIComponent(m.author)}&direction=2&head_direction=3&size=s&action=std&gesture=sml&headonly=1`}
                      alt={m.author}
                      className="hb-pixel-img relative z-10 h-8 w-8"
                      loading="lazy"
                    />
                    {badge && (
                      <img
                        src={`https://images.habbo.com/c_images/album1584/${badge.code}.gif`}
                        alt={badge.code}
                        title={`${badge.project_title}${badge.role ? " – " + badge.role : ""}`}
                        data-testid={`team-chat-badge-${m.id}`}
                        className="hb-pixel-img absolute -bottom-1 -right-1 z-20 h-4 w-4 rounded-full ring-1 ring-black/40"
                        style={{ boxShadow: `0 0 4px ${badge.color}` }}
                        loading="lazy"
                      />
                    )}
                  </div>
                )}
              </div>
              <div className={`min-w-0 flex-1 rounded-lg ${isMe ? "bg-[#7c4dff]/8" : ""} ${grouped ? "px-2 py-0.5" : "px-2 py-1"}`}>
                {!grouped && (
                  <div className="flex items-baseline gap-2 text-xs">
                    <a
                      href={`/u/${encodeURIComponent(m.author)}`}
                      className="font-bold hover:underline"
                      style={m.user_meta?.name_color ? { color: m.user_meta.name_color } : { color: "#b39bff" }}
                    >{m.author}</a>
                    {badge && (
                      <span
                        className="rounded-full px-1.5 py-0 text-[9px] font-bold tracking-wider"
                        style={{ background: `${badge.color}22`, color: badge.color }}
                        title={badge.project_title}
                        data-testid={`team-chat-badge-label-${m.id}`}
                      >{badge.project_title.split(" ")[0]}</span>
                    )}
                    {m.user_meta?.tier && m.user_meta.tier.id !== "bronze" && (
                      <span
                        className="rounded-full px-1 py-0 text-[8px] font-bold tracking-wider"
                        style={{ background: `${m.user_meta.tier.color}22`, color: m.user_meta.tier.color }}
                        title={m.user_meta.tier.label}
                      >{m.user_meta.tier.icon}</span>
                    )}
                    <span className="text-[10px] text-zinc-600">{new Date(m.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                )}
                <div className="whitespace-pre-line break-words text-sm text-zinc-200">{renderText(m.text)}</div>
              </div>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(m)}
                  data-testid={`team-chat-delete-${m.id}`}
                  className="opacity-0 group-hover/msg:opacity-100 transition-opacity rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-300 self-start"
                  title="Nachricht loeschen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 border-t border-white/10 p-3">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          placeholder="Nachricht ans Team... (z.B. @-O kommst du mal kurz?)"
          data-testid="team-chat-input"
          className="bg-[#10141b] border-white/10 text-zinc-200"
        />
        <Button onClick={onSend} disabled={!text.trim()} data-testid="team-chat-send" className="btn-primary rounded-lg px-3 py-2 text-sm">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default TeamChatPanel;
